import { createFileRoute, Link, useNavigate, redirect } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { authApi } from "@/lib/api/auth";
import { setAccessToken, getAccessToken } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import Swal from "sweetalert2";
import { BrandLogo } from "@/components/brand-logo";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useHydrated } from "@/hooks/use-hydrated";
import { absoluteUrl } from "@/lib/seo/site-url";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Log in — Dev-Hub" },
      {
        name: "description",
        content:
          "Sign in to your Dev-Hub account to access training courses, assignments, and team communication tools.",
      },
      { property: "og:title", content: "Log in — Dev-Hub" },
      {
        property: "og:description",
        content:
          "Sign in to your Dev-Hub account to access training courses, assignments, and team communication tools.",
      },
      { property: "og:url", content: absoluteUrl("/login") },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/login") }],
  }),
  beforeLoad: () => {
    // Client-side redirect handled in component useEffect to avoid hydration mismatch
  },
  component: LoginPage,
});

import { useEffect } from "react";

function LoginPage() {
  const navigate = useNavigate();
  const hydrated = useHydrated();
  const [view, setView] = useState<"login" | "forgot" | "reset">("login");

  useEffect(() => {
    if (hydrated) {
      const token = getAccessToken();
      if (token) {
        navigate({ to: "/home", replace: true });
      }
    }
  }, [hydrated, navigate]);

  // Form states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    if (!hydrated) return;
    setLoading(true);

    try {
      const response = await authApi.login({ email: email.trim(), password: password.trim() });
      if (response.token) {
        setAccessToken(response.token);
        // Trigger global event so AuthProvider updates its state
        window.dispatchEvent(new Event("auth:login-success"));
        toast.success("Welcome back");
        navigate({ to: "/home" });
      } else {
        toast.error(response.message || "Login failed");
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: error.message || "An error occurred during login",
        confirmButtonColor: "var(--primary)",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword(e: FormEvent) {
    e.preventDefault();
    if (!hydrated) return;
    setLoading(true);

    try {
      const response = await authApi.forgotPassword({ email: email.trim() });
      if (response.token) {
        setResetToken("");
        toast.success(response.token);
        setView("reset");
      } else {
        toast.error("Failed to generate recovery token");
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "An error occurred",
        confirmButtonColor: "var(--primary)",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e: FormEvent) {
    e.preventDefault();
    if (!hydrated) return;
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);

    try {
      await authApi.resetPassword({
        email: email.trim(),
        otpCode: resetToken.trim(),
        newPassword: newPassword.trim(),
      });
      toast.success("Password reset successfully. Please sign in.");
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setView("login");
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Reset Failed",
        text: error.message || "Failed to reset password",
        confirmButtonColor: "var(--primary)",
      });
    } finally {
      setLoading(false);
    }
  }

  const shellProps = {
    login: { title: "Sign in", subtitle: "Access your training dashboard" },
    forgot: { title: "Forgot password?", subtitle: "Enter your email to request a reset token" },
    reset: { title: "Reset password", subtitle: "Choose a new password using your token" },
  }[view];

  return (
    <AuthShell title={shellProps.title} subtitle={shellProps.subtitle}>
      {view === "login" && (
        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Button
                type="button"
                variant="link"
                className="px-0 font-normal text-xs text-primary hover:underline h-auto"
                onClick={() => setView("forgot")}
              >
                Forgot password?
              </Button>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                <span className="sr-only">{showPassword ? "Hide password" : "Show password"}</span>
              </Button>
            </div>
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={!hydrated || loading}
            aria-busy={loading}
          >
            {!hydrated ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </span>
            ) : loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Signing in…
              </span>
            ) : (
              "Sign in"
            )}
          </Button>
        </form>
      )}

      {view === "forgot" && (
        <form onSubmit={handleForgotPassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </div>
          <div className="space-y-2">
            <Button type="submit" className="w-full" disabled={!hydrated || loading}>
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </span>
              ) : (
                "Send Reset Token"
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setView("login")}
              disabled={loading}
            >
              Back to Sign In
            </Button>
          </div>
        </form>
      )}

      {view === "reset" && (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              readOnly
              value={email}
              autoComplete="username"
              className="bg-muted/50 cursor-not-allowed opacity-70"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="token">Recovery Token</Label>
            {/* Decoy for password managers so they don't autofill the OTP field */}
            <input
              type="text"
              name="username_decoy"
              autoComplete="username"
              style={{ opacity: 0, position: "absolute", zIndex: -1, height: 1, width: 1 }}
              tabIndex={-1}
              aria-hidden="true"
            />
            <div className="flex justify-center">
              <InputOTP
                id="recovery_token_code"
                name="recovery_token_code"
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                value={resetToken}
                onChange={(value) => setResetToken(value)}
                disabled={loading}
                autoComplete="one-time-code"
                data-1p-ignore
                data-lpignore="true"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <div className="relative">
              <Input
                id="newPassword"
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                autoComplete="new-password"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-muted-foreground hover:text-foreground"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                <span className="sr-only">
                  {showNewPassword ? "Hide password" : "Show password"}
                </span>
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                autoComplete="new-password"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-muted-foreground hover:text-foreground"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                <span className="sr-only">
                  {showConfirmPassword ? "Hide password" : "Show password"}
                </span>
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Button type="submit" className="w-full" disabled={!hydrated || loading}>
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Resetting…
                </span>
              ) : (
                "Reset Password"
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setView("login")}
              disabled={loading}
            >
              Back to Sign In
            </Button>
          </div>
        </form>
      )}
    </AuthShell>
  );
}

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div
        className="hidden lg:flex flex-col justify-between p-10 text-primary-foreground"
        style={{ background: "var(--gradient-brand)" }}
      >
        <BrandLogo className="[&_span]:text-primary-foreground" />
        <div>
          <h2 className="text-3xl font-bold leading-tight">
            A unified home for training, assessment & team communication.
          </h2>
          <p className="mt-4 text-primary-foreground/80 max-w-md">
            Secure, scalable, and ready for your team.
          </p>
        </div>
        <p className="text-xs text-primary-foreground/70">© {new Date().getFullYear()} Dev-Hub</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6">
            <BrandLogo />
          </div>
          <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}
